/**
 * FeatureFlagContext — provee acceso a los feature flags del tenant activo.
 *
 * Flujo:
 *  1. Al montarse (usuario autenticado), hace GET /features/enabled
 *  2. Almacena el dict {feature_code: bool} en estado
 *  3. Expone hasFeature(code) para que los componentes decidan qué mostrar
 *
 * Uso en componente:
 *   const { hasFeature } = useFeatureFlags();
 *   {hasFeature('ai_planning') && <Button>Generar con IA</Button>}
 */

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { ENDPOINTS } from '../endpoints';

const FeatureFlagContext = createContext({ features: {}, hasFeature: () => false, loading: true });

export const useFeatureFlags = () => useContext(FeatureFlagContext);

export const FeatureFlagProvider = ({ children }) => {
  const { token, user, isAuthenticated } = useAuth();
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);

  const loadFeatures = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setFeatures({});
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(ENDPOINTS.FEATURES_ENABLED, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFeatures(data);
      } else {
        setFeatures({});
      }
    } catch {
      setFeatures({});
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    setLoading(true);
    loadFeatures();
  }, [loadFeatures]);

  /** Verifica si un feature está habilitado para el tenant activo. */
  const hasFeature = useCallback(
    (featureCode) => {
      if (!featureCode) return false;
      // Usuarios master tienen acceso a todo
      if (user?.user_type === 'master') return true;
      return Boolean(features[featureCode]);
    },
    [features, user]
  );

  return (
    <FeatureFlagContext.Provider value={{ features, hasFeature, loading, reload: loadFeatures }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};
